import type { ReactElement } from 'react';
import { UI_DEFAULTS, type UiConfig } from '@gr/core/platform/config/ui-config';
import { ApiRoutes } from '@gr/core/api-routes';
import type { PostSort, PostStatusDto, PostSummaryDto } from '@gr/core/content/posts/dto';
import { Link, useT } from '../../../../app/di';
import { formatDate, formatDateTime } from '../../../../shared/format';
import { Badge, Tag } from '../../../../ui-kit/Badge/Badge';
import { Icon, type IconName } from '../../../../ui-kit/Icon/Icon';
import { Thumbnail } from '../../../../ui-kit/Thumbnail/Thumbnail';
import { PostDateKind, postDates } from '../model/post-dates';
import { StatusBadge } from '../StatusBadge/StatusBadge';
import styles from './PostCard.module.scss';

export interface PostCardProps {
  post: PostSummaryDto;
  status: PostStatusDto | undefined;
  /** Названия каналов, где пост уже вышел. */
  publishedIn: readonly string[];
  /** Часовой пояс блога: в нём показываются даты (POST-UC-6.CC-1). */
  timeZone: string;
  /** По какой дате отсортирован список — она выделена. */
  sortedBy: PostSort;
  /** Сколько тегов видно и когда изменение — то же создание (конфигурация ui.posts). */
  settings?: UiConfig['posts'];
}

const DATE_ICONS: Record<PostDateKind, IconName> = {
  [PostDateKind.Created]: 'calendar',
  [PostDateKind.Updated]: 'edit',
  [PostDateKind.Published]: 'send',
  [PostDateKind.Planned]: 'clock',
};

/** POST-UC-6: пост в списке — обложка, название, статус, начало текста, даты, теги, состояние планирования. */
export function PostCard({ post, status, publishedIn, timeZone, sortedBy, settings = UI_DEFAULTS.posts }: PostCardProps): ReactElement {
  const { t, tp, locale } = useT();
  return (
    <Link to="post" params={{ blogId: post.blogId, postId: post.id }} className={styles.card}>
      <div className={styles.cover}>
        <Thumbnail src={post.coverMediaId ? ApiRoutes.media.thumbnail(post.blogId, post.coverMediaId) : null} alt={post.title} square />
      </div>
      <div className={styles.body}>
        <div className={styles.head}>
          <strong className={styles.title}>{post.title}</strong>
          <StatusBadge status={status} fallbackMeta={post.meta} />
        </div>
        <p className={styles.dates}>
          {postDates(post, sortedBy, settings.dates.sameMomentSeconds).map((date) => (
            <time key={date.kind} dateTime={date.iso} className={`${styles.date} ${date.sortKey ? styles.sortKey : ''}`}>
              <Icon name={DATE_ICONS[date.kind]} size={14} />
              {t(`posts.list.dates.${date.kind}`, {
                date: date.kind === PostDateKind.Created ? formatDate(date.iso, locale, timeZone) : formatDateTime(date.iso, locale, timeZone),
              })}
            </time>
          ))}
        </p>
        {post.excerpt ? <p className={styles.excerpt}>{post.excerpt}</p> : null}
        <div className={styles.meta}>
          {post.tags.slice(0, settings.card.visibleTags).map((tag) => (
            <Tag key={tag}>{tag}</Tag>
          ))}
          {post.mediaCount > 0 ? <span className="u-muted">{tp('posts.list.media', post.mediaCount)}</span> : null}
          {post.inQueue ? <Badge tone="accent">{t('posts.list.inQueue')}</Badge> : null}
          {!post.inQueue && post.plannedCount > 0 ? <Badge tone="accent">{tp('posts.list.planned', post.plannedCount)}</Badge> : null}
          {publishedIn.length > 0 ? <Badge tone="success">{t('posts.list.publishedIn', { channels: publishedIn.join(', ') })}</Badge> : null}
        </div>
      </div>
    </Link>
  );
}
